import React, { useState } from "react";
import { ShieldCheck, Ban, RotateCcw, CheckCircle2, MessageSquareWarning, XCircle, PauseCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
  red: "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
  amber: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",
  dark: "bg-neutral-900 text-white border-neutral-900 hover:bg-neutral-800",
  plain: "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300",
};

function ConfirmDialog({ action, onClose, onConfirm, busy }) {
  const [note, setNote] = useState("");
  if (!action) return null;
  const needsNote = action.note === "required";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="quick-action-dialog" className="rounded-3xl">
        <DialogHeader>
          <DialogTitle>{action.title}</DialogTitle>
          <DialogDescription>{action.description}</DialogDescription>
        </DialogHeader>
        {action.note && (
          <textarea data-testid="quick-action-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={needsNote ? "Note for the developer (required)" : "Optional note for the developer"} className="w-full px-3 py-2 rounded-xl border border-neutral-200 focus:border-propera outline-none text-sm" />
        )}
        <DialogFooter className="gap-2">
          <button type="button" data-testid="quick-action-cancel" onClick={onClose} className={`${btn} ${TONES.plain}`}>Cancel</button>
          <button type="button" data-testid="quick-action-confirm" disabled={busy || (needsNote && !note.trim())} onClick={() => onConfirm(note.trim() || null)} className={`${btn} ${TONES[action.tone]}`}>{busy ? "Working…" : action.confirm}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useQuickAction(onDone) {
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  async function confirm(note) {
    setBusy(true);
    try { await action.run(note); toast.success(action.success); setAction(null); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }
  const dialog = <ConfirmDialog action={action} busy={busy} onClose={() => setAction(null)} onConfirm={confirm} />;
  return [setAction, dialog];
}

export function DeveloperAccountActions({ user, companyReady, onDone }) {
  const [open, dialog] = useQuickAction(onDone);
  const name = user.company_name || user.name;
  const act = (action, note) => api.patch(`/admin/users/${user.id}/action`, { action, note });
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="dev-account-actions">
      {!user.verified && (
        <button data-testid="qa-verify-developer" disabled={!companyReady} title={companyReady ? "" : "Approve all mandatory company documents first"} onClick={() => open({ title: `Verify ${name}?`, description: "Their listings will show the Verified Developer badge.", confirm: "Verify developer", tone: "green", success: "Developer verified", run: () => act("verify") })} className={`${btn} ${TONES.green}`}>
          <ShieldCheck size={13} /> Verify developer
        </button>
      )}
      {user.suspended ? (
        <button data-testid="qa-reactivate-developer" onClick={() => open({ title: `Reactivate ${name}?`, description: "They'll be able to use Propera again.", confirm: "Reactivate", tone: "green", note: "optional", success: "Account reactivated", run: (n) => act("reactivate", n) })} className={`${btn} ${TONES.green}`}>
          <RotateCcw size={13} /> Reactivate
        </button>
      ) : (
        <button data-testid="qa-suspend-developer" onClick={() => open({ title: `Suspend ${name}?`, description: "They won't be able to use the platform until reactivated.", confirm: "Suspend account", tone: "red", note: "optional", success: "Account suspended", run: (n) => act("suspend", n) })} className={`${btn} ${TONES.red}`}>
          <Ban size={13} /> Suspend
        </button>
      )}
      {dialog}
    </div>
  );
}

export function DevelopmentQuickActions({ dev, onDone }) {
  const [open, dialog] = useQuickAction(onDone);
  const set = (status, note) => api.patch(`/admin/developments/${dev.id}/review-status`, { status, note });
  const s = dev.review_status;
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={`dev-quick-actions-${dev.id}`}>
      {s !== "live" && (
        <button data-testid={`qa-approve-${dev.id}`} onClick={stop(() => open({ title: `Approve & publish ${dev.name}?`, description: "It becomes visible to investors. All mandatory documents must be approved and the developer verified.", confirm: "Approve & publish", tone: "green", success: "Development is live", run: () => set("live") }))} className={`${btn} ${TONES.green}`}><CheckCircle2 size={12} /> Approve</button>
      )}
      {!["additional_info_required", "rejected"].includes(s) && (
        <button data-testid={`qa-info-${dev.id}`} onClick={stop(() => open({ title: "Request more information", description: `Tell the developer what's missing on ${dev.name}.`, confirm: "Send request", tone: "amber", note: "required", success: "Request sent", run: (n) => set("additional_info_required", n) }))} className={`${btn} ${TONES.amber}`}><MessageSquareWarning size={12} /> More info</button>
      )}
      {s !== "rejected" && (
        <button data-testid={`qa-reject-${dev.id}`} onClick={stop(() => open({ title: `Reject ${dev.name}?`, description: "The listing will be permanently rejected and hidden.", confirm: "Reject", tone: "red", note: "required", success: "Development rejected", run: (n) => set("rejected", n) }))} className={`${btn} ${TONES.red}`}><XCircle size={12} /> Reject</button>
      )}
      {["live", "verified"].includes(s) && (
        <button data-testid={`qa-suspend-${dev.id}`} onClick={stop(() => open({ title: `Suspend ${dev.name}?`, description: "It will be hidden from investors until you publish it again.", confirm: "Suspend listing", tone: "dark", note: "optional", success: "Listing suspended", run: (n) => set("suspended", n) }))} className={`${btn} ${TONES.dark}`}><PauseCircle size={12} /> Suspend</button>
      )}
      {dialog}
    </div>
  );
}
